import React from "react";
import { motion } from "framer-motion";
import { Shield } from "lucide-react";

interface TrustBalancesReportProps {
  data: {
    balances: {
      clientId: string;
      clientName: string;
      ledgerBalanceCents: number;
      availableBalanceCents: number;
    }[];
    totalLedgerCents: number;
    totalAvailableCents: number;
  } | null;
  loading: boolean;
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const TrustBalancesReport: React.FC<TrustBalancesReportProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-32 animate-pulse">
          <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
          <div className="h-8 w-24 bg-slate-100 rounded" />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-64 animate-pulse" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Trust Held (Ledger)</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">
                {formatCurrency(data.totalLedgerCents)}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50">
              <Shield className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Available</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">
                {formatCurrency(data.totalAvailableCents)}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-blue-50">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Trust Balances Table */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Client Trust Balances</h3>
        </div>
        {data.balances.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
              <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                <Shield className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
              </div>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1.5">No trust activity</h3>
            <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
              Trust account balances and movements will be displayed once trust transactions are recorded.
            </p>
          </motion.div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">
                  Client
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase px-6 py-3">
                  Ledger Balance
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase px-6 py-3">
                  Available Balance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.balances.map((b) => (
                <tr key={b.clientId} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-3 text-sm font-medium text-slate-700">{b.clientName}</td>
                  <td className="text-right px-6 py-3">
                    <span
                      className={`text-sm font-medium ${
                        b.ledgerBalanceCents > 0 ? "text-emerald-600" : "text-slate-400"
                      }`}
                    >
                      {formatCurrency(b.ledgerBalanceCents)}
                    </span>
                  </td>
                  <td className="text-right px-6 py-3">
                    <span
                      className={`text-sm font-medium ${
                        b.availableBalanceCents > 0 ? "text-emerald-600" : "text-slate-400"
                      }`}
                    >
                      {formatCurrency(b.availableBalanceCents)}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold">
                <td className="px-6 py-3 text-sm text-slate-700">Total</td>
                <td className="text-right text-sm text-slate-800 px-6 py-3">
                  {formatCurrency(data.totalLedgerCents)}
                </td>
                <td className="text-right text-sm text-slate-800 px-6 py-3">
                  {formatCurrency(data.totalAvailableCents)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </motion.div>
    </div>
  );
};

export default TrustBalancesReport;
